import { useState } from 'react';
import { X } from 'lucide-react';
import type { Engagement, Entity, JournalEntryHeader } from '../types';
import { createJournalEntryHeader, getJournalEntryHeader } from '../db/database';
import { M_ADJ_REGISTRY } from '../utils/mAdjRegistry';
import { Button } from './Button';

interface MAdjCreateModalProps {
  engagement: Engagement;
  entities: Entity[];
  nextNumber: number;
  onCreated: (header: JournalEntryHeader) => void;
  onClose: () => void;
}

// Structural types available when creating a Custom M-Adjustment.
// Manual and specialized types (Meals50, Rollforward, etc.) are only accessible via the Standard Library tab.
type CalcType = 'IncomeStatement' | 'BS_Liability' | 'BS_Liability_Payout' | 'BS_Asset' | 'BS_Asset_Payout';

const CALC_TYPE_LABELS: Record<CalcType, string> = {
  IncomeStatement: 'Income Statement',
  BS_Liability: 'Balance Sheet \u2013 Liability',
  BS_Liability_Payout: 'Balance Sheet \u2013 Liability (with Payouts)',
  BS_Asset: 'Balance Sheet \u2013 Asset',
  BS_Asset_Payout: 'Balance Sheet \u2013 Asset (with Payouts)',
};

// Full label map used only for displaying the calcType badge on Standard Library templates,
// which may carry specialized types not available in the Custom dropdown.
const ALL_CALC_TYPE_LABELS: Record<NonNullable<import('../types').JournalEntryHeader['calcType']>, string> = {
  Manual: 'Manual',
  Standard: 'Standard',
  Meals50: 'Meals & Entertainment (50%)',
  BookTaxDiff: 'Book/Tax Difference',
  BSChange: 'Balance Sheet Change',
  Rollforward: 'Rollforward',
  IncomeStatement: 'Income Statement',
  BS_Liability: 'Balance Sheet \u2013 Liability',
  BS_Liability_Payout: 'Balance Sheet \u2013 Liability (with Payouts)',
  BS_Asset: 'Balance Sheet \u2013 Asset',
  BS_Asset_Payout: 'Balance Sheet \u2013 Asset (with Payouts)',
};

export default function MAdjCreateModal({ engagement, entities, nextNumber, onCreated, onClose }: MAdjCreateModalProps) {
  const [mode, setMode] = useState<'standard' | 'custom'>('standard');
  const [selectedEntityId, setSelectedEntityId] = useState(entities[0]?.id ?? '');
  const [selectedTemplateId, setSelectedTemplateId] = useState(M_ADJ_REGISTRY[0].id);
  const [customName, setCustomName] = useState('');
  const [customDiffType, setCustomDiffType] = useState<'Permanent' | 'Temporary'>('Permanent');
  const [customCalcType, setCustomCalcType] = useState<CalcType>('IncomeStatement');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMultiEntity = entities.length > 0;

  const handleSubmit = async () => {
    setError(null);

    let jeName: string;
    let differenceType: 'Permanent' | 'Temporary';
    let calcType: NonNullable<JournalEntryHeader['calcType']>;

    if (mode === 'standard') {
      const template = M_ADJ_REGISTRY.find((t) => t.id === selectedTemplateId);
      if (!template) return;
      jeName = template.name;
      differenceType = template.differenceType;
      calcType = template.calcType;
    } else {
      if (!customName.trim()) {
        setError('Name is required.');
        return;
      }
      jeName = customName.trim();
      differenceType = customDiffType;
      calcType = customCalcType;
    }

    setIsSubmitting(true);
    try {
      const entityId = selectedEntityId || undefined;
      const id = await createJournalEntryHeader({
        engagementId: engagement.id!,
        jeNumber: `M-${nextNumber}`,
        jeName,
        isRJE: false,
        totalDebits: 0,
        signoffState: null,
        notes: '',
        netAdjustmentCents: 0,
        updatedAt: Date.now(),
        differenceType,
        calcType,
        calcInputs: {},
        entityId,
      });
      const newHeader = await getJournalEntryHeader(id);
      if (newHeader) onCreated(newHeader);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create M-Adjustment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelClass = 'block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1';
  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-neutral-800 text-slate-800 dark:text-slate-100 transition-colors';
  const selectClass = inputClass;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
      <div className="glass-panel rounded-xl shadow-xl w-full max-w-md mx-4">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-neutral-700">
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">New M-Adjustment</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-700 transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Entity selector — only in multi-entity mode */}
        {isMultiEntity && (
          <div className="px-5 pt-4">
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
              Entity
            </label>
            <select
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-neutral-800 text-slate-800 dark:text-slate-100 transition-colors"
            >
              {entities.map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Mode tabs */}
        <div className="flex border-b border-slate-200 dark:border-neutral-700">
          {(['standard', 'custom'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
                mode === m
                  ? 'text-sage-700 dark:text-sage-300 border-b-2 border-sage-600 bg-white dark:bg-neutral-800'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-neutral-800/60'
              }`}
            >
              {m === 'standard' ? 'Standard Library' : 'Custom'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">

          {mode === 'standard' ? (
            <div>
              <label className={labelClass}>Select Adjustment Type</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className={selectClass}
              >
                {M_ADJ_REGISTRY.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {(() => {
                const t = M_ADJ_REGISTRY.find((t) => t.id === selectedTemplateId);
                return t ? (
                  <div className="mt-2 flex gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      t.differenceType === 'Permanent'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    }`}>
                      {t.differenceType}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 dark:bg-neutral-700 text-slate-600 dark:text-slate-300">
                      {t.calcType ? ALL_CALC_TYPE_LABELS[t.calcType] : ''}
                    </span>
                  </div>
                ) : null;
              })()}
            </div>
          ) : (
            <>
              <div>
                <label className={labelClass}>Name</label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="e.g. Officer Life Insurance"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className={labelClass}>Difference Type</label>
                <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-neutral-600">
                  {(['Permanent', 'Temporary'] as const).map((dt) => (
                    <button
                      key={dt}
                      onClick={() => setCustomDiffType(dt)}
                      className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                        customDiffType === dt
                          ? 'bg-purple-600 text-white'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-neutral-700'
                      }`}
                    >
                      {dt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelClass}>Calculation Type</label>
                <select
                  value={customCalcType}
                  onChange={(e) => setCustomCalcType(e.target.value as CalcType)}
                  className={selectClass}
                >
                  {(Object.keys(CALC_TYPE_LABELS) as CalcType[]).map((ct) => (
                    <option key={ct} value={ct}>{CALC_TYPE_LABELS[ct]}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-5 border-t border-slate-200 dark:border-neutral-700">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create M-Adjustment'}
          </Button>
        </div>
      </div>
    </div>
  );
}
